import { useEffect } from "react";
import { useLocation, Link, Navigate } from "react-router-dom";
import { CheckCircle2, Download } from "lucide-react";
import { GoldButton } from "@/components/ds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NEXT_STEPS = [
  "A desk officer reviews your mandate within one business day.",
  "You receive the Client Information Sheet to formalize the paper trail.",
  "Verification begins: KYC/AML, origin checks, sanctions screening.",
  "On clearance, structure and terms are agreed in writing.",
];

export default function MandateSuccess() {
  const { state } = useLocation();

  useEffect(() => { document.title = "Mandate Received — Zorem Commodities LLC"; }, []);

  if (!state?.reference) return <Navigate to="/mandate" replace />;

  return (
    <div data-testid="mandate-success" className="bg-[#0A0A0B] min-h-screen pt-40 pb-24">
      <div className="max-w-xl mx-auto px-6 text-center">
        <CheckCircle2 size={44} className="mx-auto text-[#C9A227]" />
        <h1 className="mt-8 font-display text-3xl sm:text-4xl font-medium tracking-wide text-white">
          Mandate <span className="gold-text">received.</span>
        </h1>
        <p className="mt-4 text-base text-zinc-400 leading-relaxed">
          Thank you{state.name ? `, ${state.name.split(" ")[0]}` : ""}. Your mandate has entered
          the verification queue. Quote your reference in all correspondence:
        </p>
        <div className="mt-8 card-line bg-[#121215] px-8 py-6">
          <span className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-zinc-500 block mb-2">Mandate Reference</span>
          <span className="font-mono2 text-3xl text-[#E8C85A] tracking-[0.15em]" data-testid="success-reference">{state.reference}</span>
        </div>

        <div className="mt-12 text-left">
          <h2 className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#C9A227] mb-5">What happens next</h2>
          <ol className="space-y-4">
            {NEXT_STEPS.map((s, i) => (
              <li key={i} className="flex gap-4 text-sm text-zinc-400 leading-relaxed">
                <span className="font-mono2 text-[#C9A227] shrink-0">{String(i + 1).padStart(2, "0")}</span>
                {s}
              </li>
            ))}
          </ol>
        </div>

        <div className="mt-12 space-y-4">
          <a
            href={`${API}/checklist.pdf`}
            download
            data-testid="download-checklist"
            className="w-full inline-flex items-center justify-center gap-3 px-8 py-4 text-[12px] uppercase tracking-[0.2em] font-medium text-[#D4AF37] card-line hover:bg-[#16161A] hover:text-[#E8C85A] transition-colors"
          >
            <Download size={16} /> Download the Export Documentation Checklist (PDF)
          </a>
          <p className="text-xs text-zinc-600">The 23-document checklist your file will be verified against.</p>
          <div className="pt-4 flex justify-center gap-4 flex-wrap">
            <GoldButton to="/" variant="ghost" testid="success-home">Return home</GoldButton>
            <GoldButton to="/process" variant="ghost" testid="success-process">Review the pathway</GoldButton>
          </div>
        </div>
      </div>
    </div>
  );
}
